<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue'
import logoIcon from '../assets/images/logo.png'
import qrIcon from '../assets/images/qr-code.svg'
import homeIcon from '../assets/images/home.svg'
import leftIcon from '../assets/images/arrow-circle-left.svg'
import { useUIStore } from '../stores/ui'
import { useSettingsStore } from '@/stores/settings'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
const router = useRouter()
function goBack() {
  router.back()
}

const { t } = useI18n()
const ui = useUIStore()
const settings = useSettingsStore()
const selectedLang = ref(settings.currentLang)

const showFlyout = ref(false)
function toggleFlyout() {
  showFlyout.value = !showFlyout.value
}
function closeFlyout() {
  showFlyout.value = false
}

watch(selectedLang, (newLang) => {
  settings.setLang(newLang)
})

// Glow effect state
const glowActive = ref(false)
let glowTimeout: ReturnType<typeof setTimeout> | null = null
let glowInterval: ReturnType<typeof setInterval> | null = null
const skipGlowUntil = ref(0)

function startGlow() {
  glowActive.value = true
  if (glowTimeout) clearTimeout(glowTimeout)
  glowTimeout = setTimeout(() => {
    glowActive.value = false
  }, 8000)
}

function onQrClick() {
  skipGlowUntil.value = Date.now() + 30000
  glowActive.value = false
  if (glowTimeout) clearTimeout(glowTimeout)
  ui.openQrScanner()
}

onMounted(() => {
  if (Date.now() > skipGlowUntil.value) startGlow()
  glowInterval = setInterval(() => {
    if (Date.now() > skipGlowUntil.value) startGlow()
  }, 8000)
})

onUnmounted(() => {
  if (glowInterval) clearInterval(glowInterval)
  if (glowTimeout) clearTimeout(glowTimeout)
})

function toggleTheme() {
  settings.toggleTheme()
}

function themeTooltip(): string {
  const map: Record<string, string> = {
    auto: t('themeAuto'),
    light: t('switchToDark'),
    dark: t('switchToLight'),
  }
  return map[settings.currentTheme] ?? ''
}
</script>

<template>
  <div class="toolbar">
    <div class="left-buttons">
      <img
        :src="logoIcon"
        :alt="t('logoAlt')"
        style="height: 2em; width: 3em"
        @click="toggleFlyout"
      />
      <button @click="goBack"><img :src="leftIcon" :alt="t('backAlt')" /></button>
    </div>

    <div class="middle-search">
      <button @click="onQrClick" aria-label="QR-Code scannen">
        <div v-if="glowActive" class="glow-effect"></div>
        <img :src="qrIcon" :alt="t('qrAlt')" />
      </button>
    </div>

    <div class="right-buttons">
      <div class="lang-select" :title="t('langSelectTitle')">
        <select v-model="selectedLang" :aria-label="t('langSelectTitle')">
          <option value="de">{{ t('langDe') }}</option>
          <option value="en">{{ t('langEn') }}</option>
          <option value="pl">{{ t('langPl') }}</option>
        </select>
      </div>

      <!-- Theme Switcher — 3-State: auto → light → dark → ... -->
      <button
        class="theme-toggle"
        :title="themeTooltip()"
        :aria-label="themeTooltip()"
        @click="toggleTheme"
      >
        <!-- Auto / System — Halb Sonne (links, warm) / Halb Mond (rechts, kühl) -->
        <svg
          v-if="settings.currentTheme === 'auto'"
          class="icon-auto"
          viewBox="0 0 24 24"
          fill="none"
        >
          <!-- Linker Halbkreis: Sonne -->
          <circle cx="9.5" cy="12" r="7" fill="#fbbf24" stroke="#fbbf24" stroke-width="1.6" />
          <line
            x1="9.5"
            y1="3"
            x2="9.5"
            y2="5.5"
            stroke="#fbbf24"
            stroke-width="1.6"
            stroke-linecap="round"
          />
          <line
            x1="9.5"
            y1="18.5"
            x2="9.5"
            y2="21"
            stroke="#fbbf24"
            stroke-width="1.6"
            stroke-linecap="round"
          />
          <line
            x1="3"
            y1="7"
            x2="5"
            y2="9"
            stroke="#fbbf24"
            stroke-width="1.6"
            stroke-linecap="round"
          />
          <line
            x1="14"
            y1="15"
            x2="16"
            y2="17"
            stroke="#fbbf24"
            stroke-width="1.6"
            stroke-linecap="round"
          />
          <!-- Rechter Halbkreis: Mond -->
          <path
            d="M14.5 5A7 7 0 1 0 14.5 19 7 7 0 0 1 14.5 5z"
            fill="#e8e8e8"
            stroke="#e8e8e8"
            stroke-width="1.6"
          />
        </svg>
        <!-- Light — Mond (klick → wechselt zu Dark) -->
        <svg
          v-else-if="settings.currentTheme === 'light'"
          class="icon-moon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
        <!-- Dark — Sonne (klick → wechselt zu Auto) -->
        <svg
          v-else
          class="icon-sun"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" />
          <line x1="12" y1="21" x2="12" y2="23" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
          <line x1="1" y1="12" x2="3" y2="12" />
          <line x1="21" y1="12" x2="23" y2="12" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
        </svg>
      </button>
    </div>
  </div>

  <!-- Flyout / Overlay -->
  <div v-if="showFlyout" class="flyout-overlay" @click.self="closeFlyout">
    <aside class="flyout">
      <button class="flyout-close" :aria-label="t('close')" @click="closeFlyout">×</button>
      <nav class="flyout-nav" @click="closeFlyout">
        <RouterLink to="/">{{ t('navHome') }}</RouterLink>
        <RouterLink to="/about">{{ t('navAbout') }}</RouterLink>
        <RouterLink to="/info/Brollin04">{{ t('navStudio1') }}</RouterLink>
        <RouterLink to="/info/Brollin05">{{ t('navStudio2') }}</RouterLink>
        <RouterLink to="/info/Brollin03">{{ t('navStudio3') }}</RouterLink>
        <RouterLink to="/info/Brollin10">{{ t('navManor') }}</RouterLink>
        <RouterLink to="/info/Brollin12">{{ t('navCafe') }}</RouterLink>
        <RouterLink to="/info/Brollin11">{{ t('navCircusSilo') }}</RouterLink>
        <RouterLink to="/info/Brollin14">{{ t('navFlorianHall') }}</RouterLink>
        <RouterLink to="/info/Brollin13">{{ t('navRefectorium') }}</RouterLink>
        <RouterLink to="/info/Brollin16">{{ t('navPolishGerman') }}</RouterLink>
        <RouterLink to="/info/Brollin08">{{ t('navMeadow') }}</RouterLink>
        <RouterLink to="/info/Brollin09">{{ t('navSheepStable') }}</RouterLink>
      </nav>
    </aside>
  </div>
</template>

<style scoped>
button {
  background-color: transparent;
  border: none;
}

/* Hover-Effekt */
button:hover {
  background-color: rgba(255, 255, 255, 0.1); /* leicht transparentes Weiß */
}

/* Klick-Effekt */
button:active {
  background-color: rgba(255, 255, 255, 0.3); /* etwas stärker beim Drücken */
}

/* Toolbar Layout */
.toolbar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 999;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--bg-elevated);
  border-bottom: 0px solid var(--text-secondary);
  flex-shrink: 0;
}

.toolbar .left-buttons,
.toolbar .right-buttons {
  display: flex;
  gap: 0.625em;
}

.toolbar .right-buttons {
  justify-content: flex-end;
  padding: 0.25em;
}

.toolbar .left-buttons {
  justify-content: flex-start;
  padding: 0.25em;
}

.toolbar .middle-search {
  flex: 12;
  display: flex;
  justify-content: center;
  align-items: center;
}

.toolbar .middle-search button {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  padding: 0.5em;
  border-radius: 0.5em;
}

/* Glow Effect */
.glow-effect {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 2em;
  height: 2em;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  box-shadow:
    0 0 10px 2px rgba(255, 255, 255, 0.4),
    0 0 20px 4px rgba(255, 255, 255, 0.3),
    0 0 30px 6px rgba(255, 255, 255, 0.2);
  animation: glow-pulse 2s ease-in-out infinite;
  pointer-events: none;
  z-index: -1;
}

@keyframes glow-pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: translate(-50%, -50%) scale(1);
  }
  50% {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1.1);
  }
}

.toolbar input[type='text'] {
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: transparent;
}

.toolbar img {
  width: 2em;
  height: 2em;
  display: block;
  margin: 0 auto;
}

.lang-select select {
  background: var(--bg-elevated); /* Dunkler Hintergrund */
  color: var(--text-primary); /* Weiße Schrift */
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 0.15rem 0.4rem;
  border-radius: 6px;
  font-weight: 600;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  height: 2em;
}

.lang-select select option {
  background: var(--bg-elevated); /* Dunkler Hintergrund für Optionen */
  color: var(--text-primary); /* Weiße Schrift für Optionen */
}

.lang-select {
  display: flex;
  align-items: center;
}

/* Flyout / Overlay Styles */
.flyout-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1200;
}

.flyout {
  position: fixed;
  top: 0;
  left: 0;
  width: 20rem;
  height: 100vh;
  background: var(--bg-elevated);
  color: var(--text-primary);
  padding: 1rem;
  box-shadow: 2px 0 12px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  gap: 1rem;
  z-index: 1250;
}

.flyout-close {
  align-self: flex-end;
  font-size: 1.4rem;
  line-height: 1;
  background: transparent;
  color: var(--text-primary);
  border: none;
  cursor: pointer;
}

.flyout-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex: 1 1 auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch; /* smooth scrolling on iOS */
  padding-right: 0.25rem; /* avoid content touching scrollbar */
}

.flyout-nav a {
  display: block;
  color: var(--text-secondary);
  text-decoration: none;
  padding: 0.5rem 0.25rem;
  border-radius: 4px;
  text-align: left;
  text-transform: uppercase;
}

.flyout-nav a:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* === Theme Switcher === */
.theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2em;
  height: 2em;
  border-radius: 50%;
  transition: background-color 0.3s ease;
}

.theme-toggle:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.theme-toggle svg {
  width: 1.2em;
  height: 1.2em;
}

/* Sun icon — aktiv im Dark-Modus */
.icon-sun {
  color: #fbbf24; /* warmes Gelb */
}

/* Moon icon — aktiv im Light-Modus */
.icon-moon {
  color: var(--text-primary);
}

/* Glow animation */
.glow-btn {
  animation: glow 1.5s infinite;
}

@keyframes glow {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.6);
  }
  50% {
    box-shadow: 0 0 12px 3px rgba(255, 255, 255, 0.6);
  }
}
</style>
